import { Component, Output, EventEmitter, NgZone, effect, signal, Input, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { ActivatedRoute, Router } from '@angular/router';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { FieldContextDirective } from '@app/common-codes/components/directives/FieldContext.directive';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { AccessCard } from '@app/common-codes/models/matwins-tenant/AccessCard.model';
import { LookupItem } from '@app/common-codes/services/LookupService';
import { ReactiveBadgeComponent } from '@app/common-codes/components/nested-components/reactive-badge/reactive-badge.component';
import { FieldWrapperComponent } from '@app/common-codes/components/nested-components/field-wrapper/field-wrapper.component';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { SwitchButtonComponent } from '@app/common-codes/components/nested-components/switch-button/switch-button.component';

@Register(AccessCard)
@Component({
  selector: 'app-access-card-detail',
  templateUrl: './access-card-detail.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, TranslatePipe, OnReadyDirective, ReactiveBadgeComponent, MultiSelectComponent, FieldWrapperComponent, SwitchButtonComponent, FieldContextDirective]
})
export class AccessCardDetailComponent extends GenetricDetailComponent<AccessCard> {
  override ComponentEntityClass = AccessCard;
  Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  @Input() fkPointOfServiceId: number | null = null;
  @Output() itemSaved = new EventEmitter<AccessCard>();

  accessCardStatusLookup = signal<LookupItem[]>([]);
  pointOfServiceLookup = signal<LookupItem[]>([]);

  constructor(
    route: ActivatedRoute,
    router: Router,
    translationService: TranslationService,
    validationService: GenericValidationService,
    alertModalService: AlertModalService,
    ngZone: NgZone
  ) {
    super(route, router, translationService, validationService, ngZone);
    this.alertModalService = alertModalService;

    effect(() => {
      const entity = this.editingEntity();
      if (entity) {
        // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
        this.lookupService.revision();
        this.accessCardStatusLookup.set(this.lookupService.getLookupData('AccessCardStatus'));
        this.pointOfServiceLookup.set(this.lookupService.getLookupData('PointOfService'));
        this.loadPos();
      }
    });
  }

  override getEmptyEntity(): AccessCard {
    const accessCard = new AccessCard();
    accessCard.status = 0;
    const posId = this.matwinsVariables?.posId;
    if (posId) {
      accessCard.fK_POS = posId;
    }
    return accessCard;
  }

  loadPos() {
    const entity = this.editingEntity();
    if (!entity) {
      return;
    }
    const selected = entity.fK_POS;
    if (!selected) {
      return;
    }
    entity.selectedPK_Pos = [selected.toString()];
  }

  onPOSselectChange(evt: string[]) {
    const dock = this.editingEntity();
    if (!dock) {
      return;
    }

    const selectedPosIds = (evt ?? [])
      .map(k => Number(k))
      .filter(n => Number.isFinite(n));

    dock.fK_POS = selectedPosIds[0] ?? null;
    dock.selectedPK_Pos = evt;

  }

  override parseToEntityInstance(): void {
    if (this.EntityInstance && this.editingEntity()) {
      Object.assign(this.EntityInstance, this.editingEntity());
    }
  }

  async saveItem(): Promise<void> {
    const savedItem = await this.save();
    if (savedItem) {
      this.itemSaved.emit(savedItem);
    }
  }

  async saveAndCloseItem(): Promise<void> {
    const savedItem = await this.save();
    if (savedItem) {
      this.itemSaved.emit(savedItem);
      this.closeModal.emit();
    }
  }

  onStatusChange(newStatus: number): void {
    const currentEntity = this.editingEntity();
    if (currentEntity) {
      const newEntity = Object.assign(this.getEmptyEntity(), currentEntity);
      newEntity.status = newStatus;
      this.editingEntity.set(newEntity);
    }
  }

  /// Quand on révoque la carte, on force le statut à Unavailable (0)
  onIsRevokedChange(value: boolean): void {
    // Statut Unavailable = 0, Available = 1
    this.updateEditingEntity('isRevoked', value);
    if (value) {
      this.updateEditingEntity('status', 0);
    }
  }

  /// Permission spécifique pour révoquer une carte d'accès
  public get canRevokeCard(): boolean {
    return true || this.permissionsService.UserHasSinglePermission('Permissions.Entities.AccessCard.Revoke');
  }
}
